import React, { useState } from 'react';
import { Button, ConfigProvider, Form, Input, Modal, Select, message as antMessage } from 'antd';
import { Helmet } from 'react-helmet';
import emailjs from '@emailjs/browser';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import { company } from '../data/siteData';

const ContactPage = () => {
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [sending, setSending] = useState(false);

  const onFinish = async (values) => {
    setSending(true);
    try {
      await emailjs.send(
        'service_os08vih',
        'template_trpw6mw',
        values,
        'Q4qUM5EApWqQOFFHo'
      );
      setModalVisible(true);
      form.resetFields();
    } catch (error) {
      console.error('送信エラー:', error);
      antMessage.error('送信に失敗しました。再度お試しください。');
    } finally {
      setSending(false);
    }
  };

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'お問い合わせ | RYU株式会社',
    description: '法人仕入れ・見積もり・各種ご相談はこちらのフォームよりお問い合わせください。',
    url: 'https://www.ryukk.jp/contact',
    mainEntity: {
      '@type': 'Organization',
      name: company.name,
      url: 'https://www.ryukk.jp',
      logo: 'https://www.ryukk.jp/logo.png',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+81-495-71-5696',
        contactType: 'customer support',
        areaServed: 'JP',
        availableLanguage: ['Japanese', 'Chinese'],
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>お問い合わせ | RYU株式会社</title>
        <meta
          name="description"
          content="酒類卸売、法人仕入れ、商品提案、継続取引、物流・ECに関するお問い合わせ・見積もり依頼はこちらから承ります。"
        />
        <meta property="og:title" content="お問い合わせ | RYU株式会社" />
        <meta property="og:description" content="酒類流通と法人取引に関するご相談はこちら。" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.ryukk.jp/contact" />
        <meta property="og:image" content="https://www.ryukk.jp/ogp.jpg" />
        <link rel="canonical" href="https://www.ryukk.jp/contact" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      <PageHero
        label="CONTACT"
        title={<>新しい取引を、<br />ここから。</>}
        description="法人仕入れ・見積もり・商品提案など、お気軽にご相談ください。"
      />

      <section className="contact-page section">
        <div className="container contact-page__grid">
          <Reveal className="contact-page__aside">
            <p className="eyebrow">BUSINESS INQUIRY</p>
            <h2>ご相談内容に合わせ、<br />担当者が対応します。</h2>
            <p>
              お取引条件、取扱商品、納期、数量など、可能な範囲で具体的にご記入ください。
              内容を確認のうえ、担当者よりご連絡いたします。
            </p>
            <dl>
              <div>
                <dt>PHONE / FAX</dt>
                <dd><a href={`tel:${company.phone.replaceAll('-', '')}`}>{company.phone}</a></dd>
              </div>
              <div>
                <dt>E-MAIL</dt>
                <dd><a href={`mailto:${company.email}`}>{company.email}</a></dd>
              </div>
            </dl>
          </Reveal>

          <Reveal className="contact-form" delay={100}>
            <ConfigProvider
              theme={{
                token: {
                  colorPrimary: '#c01020',
                  colorText: '#f2efe7',
                  colorTextPlaceholder: '#777a7e',
                  colorBorder: '#3c4046',
                  colorBgContainer: '#11151b',
                  borderRadius: 0,
                  controlHeight: 52,
                  fontFamily: '"Helvetica Neue", Arial, "Hiragino Sans", "Yu Gothic", Meiryo, sans-serif',
                },
              }}
            >
              <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>
                <div className="contact-form__row">
                  <Form.Item name="company" label="会社名">
                    <Input autoComplete="organization" placeholder="任意" />
                  </Form.Item>
                  <Form.Item name="name" label="お名前">
                    <Input autoComplete="name" placeholder="任意" />
                  </Form.Item>
                </div>
                <Form.Item
                  name="email"
                  label="メールアドレス"
                  rules={[
                    { required: true, message: 'メールアドレスを入力してください。' },
                    { type: 'email', message: '正しいメールアドレスを入力してください。' },
                  ]}
                >
                  <Input type="email" autoComplete="email" placeholder="必須" />
                </Form.Item>
                <Form.Item name="subject" label="お問い合わせ種別" initialValue="ご注文">
                  <Select
                    options={[
                      { value: '法人取引', label: '法人取引について' },
                      { value: 'ご注文', label: 'ご注文について' },
                      { value: '見積もり', label: '見積もりについて' },
                      { value: 'その他', label: 'その他' },
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  name="message"
                  label="お問い合わせ内容"
                  rules={[{ required: true, message: '内容をご記入ください。' }]}
                >
                  <Input.TextArea rows={7} placeholder="できるだけ詳しくご記入ください。" />
                </Form.Item>
                <p className="contact-form__notice">
                  ご入力いただいた情報は、お問い合わせへの対応のために利用します。
                </p>
                <Button className="contact-form__submit" type="primary" htmlType="submit" loading={sending}>
                  {sending ? '送信中' : '送信する'}
                  <span aria-hidden="true">↗</span>
                </Button>
              </Form>
            </ConfigProvider>
          </Reveal>
        </div>
      </section>

      <Modal
        title="送信完了"
        open={modalVisible}
        onOk={() => setModalVisible(false)}
        onCancel={() => setModalVisible(false)}
        okText="閉じる"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        お問い合わせいただきありがとうございました。確認のうえ、担当よりご連絡させていただきます。
      </Modal>
    </>
  );
};

export default ContactPage;
